import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import { patientQueries } from '@/features/patient-record/queries';
import { mapCarePathway } from '@/features/patient-record/mappers/carePathway';
import type {
  CarePathwayStatus,
  CarePathwayStepStatus,
  CarePathwayViewModel,
} from '@/features/patient-record/types';

/**
 * Care-pathway mutations (PTV2-1174). Per-action hooks against the new
 * `/api/v1/persons/{guid}/care-pathways*` endpoints. Each mutation owns
 * its own optimistic + rollback against `patientQueries.carePathways(guid)`
 * and invalidates on settle so server-assigned audit fields land.
 *
 * Notes on the new contract (PTV2-1173):
 *   - Step status: `NOT_STARTED | IN_PROGRESS | COMPLETED | SKIPPED`.
 *     Skipping requires `skip_rationale`; completing accepts `completion_note`.
 *     The BE auto-advances the next `NOT_STARTED` step to `IN_PROGRESS`
 *     when a step is completed/skipped, so the optimistic update mirrors that.
 *   - Pathway status: COMPLETED / CANCELLED both require `reason`; the FE
 *     surfaces a dialog upstream so this hook only sees populated reasons.
 */

export interface AssignCarePathwayInput {
  readonly templateId: string;
  readonly templateName: string;
  readonly templateSteps: readonly {
    readonly id: string;
    readonly name: string;
    readonly description: string | null;
    readonly sortOrder: number;
  }[];
}

export interface UpdateStepInput {
  readonly pathwayId: string;
  readonly stepId: string;
  readonly status: CarePathwayStepStatus;
  readonly completionNote?: string;
  readonly skipRationale?: string;
}

export interface UpdateStatusInput {
  readonly pathwayId: string;
  readonly status: CarePathwayStatus;
  readonly reason?: string;
}

interface MutationContext {
  readonly previous: CarePathwayViewModel[] | undefined;
}

type AssignResult = UseMutationResult<
  CarePathwayViewModel,
  Error,
  AssignCarePathwayInput,
  MutationContext
>;
type RemoveResult = UseMutationResult<undefined, Error, string, MutationContext>;
type UpdateStepResult = UseMutationResult<
  CarePathwayViewModel,
  Error,
  UpdateStepInput,
  MutationContext
>;
type UpdateStatusResult = UseMutationResult<
  CarePathwayViewModel,
  Error,
  UpdateStatusInput,
  MutationContext
>;

export function useCarePathwayMutations(
  guid: string,
): {
  readonly assign: AssignResult;
  readonly remove: RemoveResult;
  readonly updateStep: UpdateStepResult;
  readonly updateStatus: UpdateStatusResult;
} {
  const queryClient = useQueryClient();
  const { queryKey } = patientQueries.carePathways(guid);

  const snapshot = (): MutationContext => ({
    previous: queryClient.getQueryData(queryKey),
  });

  const rollback = (ctx: MutationContext | undefined): void => {
    if (ctx === undefined) return;
    queryClient.setQueryData(queryKey, ctx.previous);
  };

  const settle = (): void => {
    queryClient.invalidateQueries({ queryKey }).catch(() => undefined);
  };

  const assign = useMutation<
    CarePathwayViewModel,
    Error,
    AssignCarePathwayInput,
    MutationContext
  >({
    meta: { successMessage: 'success.toast.title.care_pathway_created' },
    mutationFn: async (input) => {
      const dto = await apiClient.assignCarePathway(guid, {
        template_id: input.templateId,
      });
      return mapCarePathway(dto);
    },
    onMutate: (input) => {
      const ctx = snapshot();
      // BE marks the first step IN_PROGRESS on enroll; mirror that locally.
      const optimistic: CarePathwayViewModel = {
        id: `optimistic-${Date.now().toString(36)}`,
        name: input.templateName,
        description: '',
        status: 'ACTIVE',
        templateId: input.templateId,
        completionReason: null,
        enrolledAt: new Date().toISOString(),
        enrolledBy: null,
        closedAt: null,
        closedBy: null,
        stepsCompleted: 0,
        stepsTotal: input.templateSteps.length,
        steps: input.templateSteps.map((s, idx) => ({
          id: s.id,
          name: s.name,
          description: s.description ?? '',
          status: idx === 0 ? 'IN_PROGRESS' : 'NOT_STARTED',
          sortOrder: s.sortOrder,
          completionNote: null,
          skipRationale: null,
          completedAt: null,
          completedBy: null,
        })),
      };
      queryClient.setQueryData(queryKey, [optimistic, ...(ctx.previous ?? [])]);
      return ctx;
    },
    onError: (_error, _input, ctx) => {
      rollback(ctx);
    },
    onSettled: settle,
  });

  const remove = useMutation<undefined, Error, string, MutationContext>({
    meta: { successMessage: 'success.toast.title.care_pathway_deleted' },
    mutationFn: (id) => apiClient.removeCarePathway(guid, id),
    onMutate: (id) => {
      const ctx = snapshot();
      queryClient.setQueryData<CarePathwayViewModel[]>(
        queryKey,
        (ctx.previous ?? []).filter((cp) => cp.id !== id),
      );
      return ctx;
    },
    onError: (_e, _v, ctx) => {
      rollback(ctx);
    },
    onSettled: settle,
  });

  const updateStep = useMutation<
    CarePathwayViewModel,
    Error,
    UpdateStepInput,
    MutationContext
  >({
    mutationFn: async (input) => {
      const dto = await apiClient.updateCarePathwayStep(
        guid,
        input.pathwayId,
        input.stepId,
        {
          status: input.status,
          ...(input.completionNote !== undefined
            ? { completion_note: input.completionNote }
            : {}),
          ...(input.skipRationale !== undefined
            ? { skip_rationale: input.skipRationale }
            : {}),
        },
      );
      return mapCarePathway(dto);
    },
    onMutate: (input) => {
      const ctx = snapshot();
      const completing =
        input.status === 'COMPLETED' || input.status === 'SKIPPED';
      const next = (ctx.previous ?? []).map((cp) => {
        if (cp.id !== input.pathwayId) return cp;
        const steps = cp.steps.map((step) => {
          if (step.id !== input.stepId) return step;
          if (input.status === 'NOT_STARTED') {
            return {
              ...step,
              status: input.status,
              completedAt: null,
              completedBy: null,
              completionNote: null,
              skipRationale: null,
            };
          }
          return {
            ...step,
            status: input.status,
            completedAt: completing ? new Date().toISOString() : step.completedAt,
            completedBy: step.completedBy ?? null,
            completionNote:
              input.status === 'COMPLETED'
                ? input.completionNote ?? step.completionNote
                : step.completionNote,
            skipRationale:
              input.status === 'SKIPPED'
                ? input.skipRationale ?? step.skipRationale
                : step.skipRationale,
          };
        });
        // Mirror BE auto-advance: bump the first NOT_STARTED step to
        // IN_PROGRESS when the edit completed/skipped a step.
        const withAdvance = completing ? bumpNextNotStarted(steps) : steps;
        const stepsCompleted = withAdvance.filter(
          (s) => s.status === 'COMPLETED' || s.status === 'SKIPPED',
        ).length;
        return { ...cp, steps: withAdvance, stepsCompleted };
      });
      queryClient.setQueryData(queryKey, next);
      return ctx;
    },
    onError: (_e, _v, ctx) => {
      rollback(ctx);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData<CarePathwayViewModel[]>(queryKey, (list) =>
        (list ?? []).map((cp) => (cp.id === updated.id ? updated : cp)),
      );
    },
    onSettled: settle,
  });

  const updateStatus = useMutation<
    CarePathwayViewModel,
    Error,
    UpdateStatusInput,
    MutationContext
  >({
    mutationFn: async (input) => {
      const dto = await apiClient.updateCarePathwayStatus(guid, input.pathwayId, {
        status: input.status,
        ...(input.reason !== undefined ? { reason: input.reason } : {}),
      });
      return mapCarePathway(dto);
    },
    onMutate: (input) => {
      const ctx = snapshot();
      const closing = input.status === 'COMPLETED' || input.status === 'CANCELLED';
      const next = (ctx.previous ?? []).map((cp) =>
        cp.id === input.pathwayId
          ? {
              ...cp,
              status: input.status,
              completionReason: closing
                ? input.reason ?? cp.completionReason
                : cp.completionReason,
              closedAt: closing ? new Date().toISOString() : cp.closedAt,
            }
          : cp,
      );
      queryClient.setQueryData(queryKey, next);
      return ctx;
    },
    onError: (_e, _v, ctx) => {
      rollback(ctx);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData<CarePathwayViewModel[]>(queryKey, (list) =>
        (list ?? []).map((cp) => (cp.id === updated.id ? updated : cp)),
      );
    },
    onSettled: settle,
  });

  return { assign, remove, updateStep, updateStatus };
}

function bumpNextNotStarted<
  T extends { readonly status: CarePathwayStepStatus },
>(steps: readonly T[]): T[] {
  const idx = steps.findIndex((s) => s.status === 'NOT_STARTED');
  if (idx === -1) return [...steps];
  const nextStatus: CarePathwayStepStatus = 'IN_PROGRESS';
  return steps.map((s, i) => (i === idx ? { ...s, status: nextStatus } : s));
}
